import React, { useState } from 'react';
import {
  FiImage,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiX,
  FiCheck,
  FiEye,
  FiEyeOff,
  FiLink,
  FiSend,
  FiCopy,
  FiExternalLink,
  FiFolder,
  FiGrid,
  FiMessageCircle
} from 'react-icons/fi';

const DOWNLOAD_POLICIES = [
  { value: 'none', label: 'No Downloads' },
  { value: 'lowres', label: 'Low Resolution' },
  { value: 'highres', label: 'High Resolution' },
  { value: 'selected', label: 'Selected Only' }
];

function formatDate(value) {
  if (!value) return ', ';
  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

export default function GalleryCard({ gallery, onEdit, onDelete, onPublish, onUnpublish, onCreateToken, onCreateSelectionRequest, onAddPhotos, onRevokeToken, onReviewFeedback, pendingAnnotations = 0 }) {
  const isPublished = gallery.publish_state === 'published';
  const isDirect = gallery.source_type === 'direct';
  const activeTokens = (gallery.tokens || []).filter(t => !t.revoked_at);
  const hasTokens = activeTokens.length > 0;
  const [copiedId, setCopiedId] = useState(null);

  const copyShareUrl = (token, e) => {
    e.stopPropagation();
    if (!token.token) return;
    const url = `${window.location.origin}/g/${token.token}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(token.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const handleRevoke = (token, e) => {
    e.stopPropagation();
    if (confirm('Revoke this share link? Clients will no longer be able to access the gallery with this link.')) {
      onRevokeToken(token.id);
    }
  };

  return (
    <div className={`gallery-card ${isPublished ? 'published' : 'draft'}`}>
      <div className="gallery-card-header">
        <div className="gallery-card-title">
          <h4>{gallery.title}</h4>
          <span className={`gallery-type-badge ${gallery.type}`}>{gallery.type}</span>
        </div>
        <div className="gallery-card-header-actions">
          {isPublished ? (
            <span className="status-pill status-published">
              <FiEye size={12} /> Published
            </span>
          ) : (
            <span className="status-pill status-draft">
              <FiEyeOff size={12} /> Draft
            </span>
          )}
          <button onClick={() => onEdit(gallery)} className="icon-btn" title="Edit">
            <FiEdit2 size={16} />
          </button>
          <button onClick={() => onDelete(gallery)} className="icon-btn danger" title="Delete">
            <FiTrash2 size={16} />
          </button>
        </div>
      </div>

      {gallery.description && (
        <p className="gallery-card-description">{gallery.description}</p>
      )}

      {pendingAnnotations > 0 && (
        <div className="gallery-annotation-badge">
          <FiMessageCircle size={14} />
          <span>{pendingAnnotations} pending annotation{pendingAnnotations !== 1 ? 's' : ''}</span>
        </div>
      )}

      <div className="gallery-card-meta">
        <span><FiImage size={14} /> {gallery.image_count || 0} photos</span>
        {isDirect ? (
          <span><FiGrid size={14} /> Direct selection</span>
        ) : gallery.album_title ? (
          <span><FiFolder size={14} /> {gallery.album_title}</span>
        ) : null}
        <span>Created {formatDate(gallery.created_at)}</span>
        {gallery.published_at && (
          <span>Published {formatDate(gallery.published_at)}</span>
        )}
      </div>

      <div className="gallery-card-settings">
        <span className={gallery.watermark_enabled ? 'enabled' : 'disabled'}>
          {gallery.watermark_enabled ? 'Watermark On' : 'No Watermark'}
        </span>
        <span>
          Downloads: {DOWNLOAD_POLICIES.find(p => p.value === gallery.download_policy)?.label || 'None'}
        </span>
      </div>

      {isPublished && hasTokens && (
        <div className="gallery-share-links">
          <div className="share-links-header">
            <FiLink size={14} /> Active Share Links ({activeTokens.length})
          </div>
          {activeTokens.map(token => (
            <div key={token.id} className="share-link-item">
              {token.token ? (
                <div className="share-link-url-row">
                  <input
                    type="text"
                    readOnly
                    value={`${window.location.origin}/g/${token.token}`}
                    className="share-link-url-input"
                    onClick={e => e.target.select()}
                  />
                  <button
                    onClick={(e) => copyShareUrl(token, e)}
                    className="share-link-copy-btn"
                    title="Copy link"
                  >
                    {copiedId === token.id ? <FiCheck size={14} /> : <FiCopy size={14} />}
                  </button>
                  <a
                    href={`${window.location.origin}/g/${token.token}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="share-link-open-btn"
                    title="Open in new tab"
                    onClick={e => e.stopPropagation()}
                  >
                    <FiExternalLink size={14} />
                  </a>
                  <button
                    onClick={(e) => handleRevoke(token, e)}
                    className="share-link-revoke-btn"
                    title="Revoke link"
                  >
                    <FiX size={14} />
                  </button>
                </div>
              ) : (
                <div className="share-link-legacy">
                  <span className="share-link-prefix">Link: {token.token_prefix}...</span>
                  <span className="share-link-legacy-note">(Legacy - create new link for copyable URL)</span>
                  <button
                    onClick={(e) => handleRevoke(token, e)}
                    className="share-link-revoke-btn legacy"
                    title="Revoke link"
                  >
                    <FiX size={12} /> Revoke
                  </button>
                </div>
              )}
              <span className="share-link-meta">
                {token.open_count || 0} views
                {token.expires_at && ` · Expires ${formatDate(token.expires_at)}`}
                {!token.expires_at && ' · No expiry'}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="gallery-card-actions">
        {!isPublished && (
          <>
            <button onClick={() => onPublish(gallery)} className="client-action-btn primary">
              <FiSend /> Publish & Share
            </button>
            {isDirect && (
              <button onClick={() => onAddPhotos(gallery)} className="client-action-btn">
                <FiPlus /> Add Photos
              </button>
            )}
          </>
        )}
        {isPublished && (
          <>
            <button onClick={() => onReviewFeedback(gallery)} className="client-action-btn">
              <FiMessageCircle /> Review Feedback
            </button>
            <button onClick={() => onCreateToken(gallery)} className="client-action-btn">
              <FiLink /> New Share Link
            </button>
            <button onClick={() => onCreateSelectionRequest(gallery)} className="client-action-btn">
              <FiSend /> New Selection
            </button>
            <button onClick={() => onUnpublish(gallery)} className="client-action-btn warning">
              <FiEyeOff /> Unpublish
            </button>
          </>
        )}
      </div>
    </div>
  );
}
